// @ts-strict-ignore
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ArrayUtils } from '@shared/util/array-utils';
import { StringUtils } from '@shared/util/string-utils';
import { ProjectUtils } from '../util/project-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ProjectModel } from '../../../model/project.model';
import { ProjectSaveInfoVO } from '../../../model/vo/project.vo';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { UserActionsLoggingManager } from '@user/service/logging/user-actions-logging.manager';
import { ProjectOrderDTO } from '@article/model/vo/project-order.dto';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { ProjectStatesDef } from '../constants/project-states-def';
import { ProjectDataService } from '../../../service/project-data.service';
import { lastValueFrom } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-project-direct-order',
  templateUrl: './project-direct-order.component.html'
})
export class ProjectDirectOrderComponent implements OnInit {

  public projectSaveInfo: ProjectSaveInfoVO = null;
  public directlyOrderableVersionExists: boolean = false;
  public directlyOrderableVersionMatchesCurrentlyLoadedVersion: boolean = false;

  constructor(private bsModalRef: BsModalRef,
              private cdRef: ChangeDetectorRef,
              private projectDataService: ProjectDataService,
              private projectModel: ProjectModel,
              private localeResourceManager: LocaleResourceManager,
              private alertHelper: AlertHelper,
              private userActionsLoggingManager: UserActionsLoggingManager) {
  }

  public ngOnInit(): void {
    this.projectDataService.loadProjectSaveInfo(this.projectModel.currentProject.id).subscribe(
      (saveInfo: ProjectSaveInfoVO) => {
        this.projectSaveInfo = saveInfo;
        this.directlyOrderableVersionExists = saveInfo.highestFinalizedProjectVersion > -1 && !StringUtils.isEmpty(saveInfo.highestFinalizedProjectVersionArticleNumberLong);
        this.directlyOrderableVersionMatchesCurrentlyLoadedVersion = saveInfo.highestFinalizedProjectVersion === this.projectModel.currentProject.currentVersion;
        this.cdRef.markForCheck();
      },
      (error: any) => this.projectSaveInfo = null
    );
  }

  public getInfoMessage(): string {
    let infoMessage: string = '';
    if (this.directlyOrderableVersionExists) {
      if (this.directlyOrderableVersionMatchesCurrentlyLoadedVersion) {
        infoMessage += this.localeResourceManager.getValueByKey('projectDirectOrderPopup.message.info_identical_existing_version_to_order');
      } else {
        infoMessage += this.localeResourceManager.getValueByKey('projectDirectOrderPopup.message.info_different_existing_version_to_order');
      }
    } else {
      if (this.projectModel.currentProject.finalizationState === ProjectStatesDef.FINALIZATION_STATE_REQUESTED) {
        infoMessage += this.localeResourceManager.getValueByKey('projectDirectOrderPopup.message.info_no_version_to_order_and_quote_requested');
      } else {
        infoMessage += this.localeResourceManager.getValueByKey('projectDirectOrderPopup.message.info_no_version_to_order');
      }
    }
    return infoMessage;
  }

  public getArticleNumberLongLabel(): string {
    let articleNumberLong: string = this.projectSaveInfo.highestFinalizedProjectVersionArticleNumberLong;
    return this.localeResourceManager.getValueByKey('projectDirectOrderPopup.label.article_number_long', { 0: articleNumberLong});
  }

  public getProjectVersionDisplayStringLabel(): String {
    let projectVersionDisplayString: string = ProjectUtils.getHighestFinalizedVersionDisplayStringFromProjectSaveInfo(this.projectSaveInfo, true);
    return this.localeResourceManager.getValueByKey('projectDirectOrderPopup.label.revision_index', { 0: projectVersionDisplayString});
  }

  public close(): void {
    this.bsModalRef.hide();
  }

  public async order(): Promise<void> {
    this.userActionsLoggingManager.logFunction('direct_order', this.projectSaveInfo.highestFinalizedProjectVersionArticleNumberLong);
    const projectOrder: ProjectOrderDTO = new ProjectOrderDTO();
    projectOrder.language = this.localeResourceManager.currentLanguage.langCode;
    projectOrder.orderType = "ESHOP_REORDER";
    projectOrder.projectId = this.projectSaveInfo.projectId;
    projectOrder.projectVersion = this.projectSaveInfo.highestFinalizedProjectVersion;
    const projectOrderResult = await lastValueFrom(this.projectDataService.orderProject(projectOrder));
    if (projectOrderResult.orderSuccessful && !StringUtils.isEmpty(projectOrderResult.eshopCartUrl)) {
      // such alert is necessary, because otherwise opened window could get blocked by browser,
      // because it would be opened on reponse from server and that would not be considered to be
      // based on direct user interaction:
      const answer = await this.alertHelper.showAlert(
        'projectDirectOrderPopup.title',
        'projectDirectOrderPopup.message.shopping_cart_successfully_updated',
        null,
        [AlertButton.YES, AlertButton.NO],
        '',
        {
          yesLabel: 'projectDirectOrderPopup.button.to_shopping_cart',
          noLabel: 'global.label.close'
        }
      );
      if (answer === AlertButton.YES) {
        window.open(projectOrderResult.eshopCartUrl, '_blank');
      }
      this.close();
    } else if (ArrayUtils.isNotEmpty(projectOrderResult.unavailableProductCodes)) {
      await this.alertHelper.showAlert('global.title.error', 'projectDirectOrderPopup.message.project_unavailable_in_eshop', null, [AlertButton.OK]);
    } else if (ArrayUtils.isNotEmpty(projectOrderResult.outOfStockProductCodes)) {
      await this.alertHelper.showAlert('global.title.error', 'projectDirectOrderPopup.message.project_out_of_stock_in_eshop', null, [AlertButton.OK]);
    } else {
      await this.alertHelper.showAlert('global.title.error', 'global.message.unexpected_error', null, [AlertButton.OK]);
    }
  }

}
